<template>
  <ul v-if="hasListingAccessories" :class="$style.links">
    <li v-if="hasVirtualTour" :class="$style.linkItem">
      <HgLink icon="Arrow360" tag="button" @click="openListingGalleryModalOnVirtualTourTab">
        {{ t('buttons.virtualTour') }}
      </HgLink>
    </li>
    <li v-if="hasVideo" :class="$style.linkItem">
      <HgLink icon="Video" tag="button" @click="openListingGalleryModalOnVideoTab">
        {{ t('buttons.videoMovie') }}
      </HgLink>
    </li>
    <li v-if="hasListingDocument" :class="$style.linkItem">
      <HgLink
        icon="Document"
        :href="listingDocument.href"
        rel="noopener noreferrer"
        target="_blank"
        @click="emit('documentClick')"
      >
        {{ listingDocument.label || t('buttons.attachedDocument') }}
      </HgLink>
    </li>
    <li v-if="$slots.default" :class="$style.linkItem">
      <slot></slot>
    </li>
  </ul>
</template>

<script lang="ts" setup>
import { HgLink } from '@smg-real-estate/domus';
import { L10N } from '@smg-real-estate/hg-rets-schema';

import {
  getFirstListingAttachedDocument,
  getListingVideoMovie,
  getListingVirtualTour,
} from '@/helpers/ListingData/ListingData';
import { HgListingAttachedDocument, ListingDetailsModalTabId } from '@/models';
import { useListingImageGalleryStore } from '@/store/modules/listing-image-gallery';
import { computed, useSlots } from 'vue';
import { useI18n } from 'vue-i18n';

const slots = useSlots();
const { t } = useI18n();
const listingImageGalleryStore = useListingImageGalleryStore();

defineOptions({
  name: 'ListingAccessories',
});

interface Props {
  listingDefaultLocale: L10N | undefined;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  virtualTourClick: [];
  videoClick: [];
  documentClick: [];
}>();

const hasListingAccessories = computed((): boolean => {
  return hasVirtualTour.value || hasVideo.value || hasListingDocument.value || !!slots.default;
});
const hasVirtualTour = computed((): boolean => {
  return !!props.listingDefaultLocale && !!getListingVirtualTour(props.listingDefaultLocale);
});
const hasVideo = computed((): boolean => {
  return !!props.listingDefaultLocale && !!getListingVideoMovie(props.listingDefaultLocale);
});
const hasListingDocument = computed((): boolean => {
  return !!listingDocument.value && !!listingDocument.value.href;
});
const listingDocument = computed((): HgListingAttachedDocument => {
  return getFirstListingAttachedDocument(props.listingDefaultLocale!) as HgListingAttachedDocument;
});
function openListingGalleryModalOnVirtualTourTab(): void {
  listingImageGalleryStore.setSelectedModalTabId(ListingDetailsModalTabId.MODAL_TAB_ID_VIRTUALTOUR);
  listingImageGalleryStore.openModal();
  emit('virtualTourClick');
}

function openListingGalleryModalOnVideoTab(): void {
  listingImageGalleryStore.setSelectedModalTabId(ListingDetailsModalTabId.MODAL_TAB_ID_VIDEOMOVIE);
  listingImageGalleryStore.openModal();
  emit('videoClick');
}
</script>

<style lang="scss" module>
@import '@/assets/variables';

$link-min-width: 70px;
$link-min-width-vp-large: 55px;
$link-label-max-width: 200px;
$listing-accessories-min-height: 2em;
$item-max-width: 460px;

.links {
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
  gap: $space-100;
  max-width: 100%;
  min-height: $listing-accessories-min-height;
  overflow: hidden;

  @include menu-ul-reset;
}

.linkItem {
  @include body-2;

  min-width: $link-min-width;
  vertical-align: middle;

  a {
    span {
      display: block;
      max-width: $item-max-width;

      @include truncate-text;
    }
  }

  button {
    font-size: var(--typo-body-2-font-size);
    line-height: var(--typo-body-2-line-height);
  }
}
</style>
